import { Help, Info } from "@mui/icons-material";
import { Box, Divider, Link, styled, TableCell, useMediaQuery, useTheme } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { useMortgageState } from "../../../contexts/mortgage/Reducer";
import { AnalyticsActivity, AppComponent } from "../../../services/event-service/EventService";
import { useEventService } from "../../../services/event-service/EventServiceContext";
import { addYears, DateFormat, formatDate } from "../../../utils/DateUtils";
import { formatAmount, formatInterestRate, parseIBAN } from "../../../utils/NumberUtils";
import { renderProductType } from "../../../utils/Utils";
import ErrorMessage from "../../feedback/ErrorMessage";
import MortgageTooltip from "../../tooltip/MortgageTooltip";
import { MortgageSummaryBold, MortgageSummaryHeaderLabel, MortgageSummaryLabel } from "../2-summary/NewMortgageSummary";
import DetailsPopupScreen from "../popups/mortgage-details/MortgageDetailsPopupScreen";

const DetailsLink = styled(Link)(() => ({
    color: "#0093D1",
    cursor: "pointer",
    fontSize: "18px",
    fontWeight: 300,
    marginTop: "3px",
    ariaHasPopup: "dialog",
}));

const ConfigurationSummary = () => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const matchesWebView = useMediaQuery(theme.breakpoints.up("lg"));

    const { newMortgage } = useMortgageState();
    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
    const eventService = useEventService();

    const showDetailsDialog = () => {
        eventService.sendAnalyticsEvent(AnalyticsActivity.SHOW_MORTGAGE_DETAILS, AppComponent.CONFIGURATION_SCREEN);
        setDetailsDialogOpen(true);
    };

    const closeDetailsDialog = () => {
        setDetailsDialogOpen(false);
    };

    if (newMortgage === undefined) {
        return <ErrorMessage />;
    }
    const isDurationUpdated = () => {
        return Number(newMortgage.duration) !== 0;
    };

    const renderMobileView = () => {
        return (
            <Box mt={"40px"} data-cy={"configuration-summary-mobile"}>
                <Box width={"100%"} paddingBottom={"16px"}>
                    <Box>
                        <MortgageSummaryBold data-cy={"summary-product-type"}>
                            {renderProductType(newMortgage, i18n, t)}
                        </MortgageSummaryBold>
                    </Box>
                    <Box sx={{ marginBottom: "16px" }}>
                        <MortgageSummaryLabel data-cy={"summary-duration-info"}>
                            {newMortgage.duration} {t("General.Years")} /{" "}
                            {formatDate(newMortgage.expirationDate, DateFormat.DATE)} -{" "}
                            {addYears(newMortgage.expirationDate, +newMortgage.duration)}
                        </MortgageSummaryLabel>
                    </Box>
                    <Box display={"flex"}>
                        <Box flex={1}>
                            <Box>
                                <MortgageSummaryHeaderLabel data-cy={"mnthly-charge"}>
                                    {t("General.Monthly charge")}
                                </MortgageSummaryHeaderLabel>
                            </Box>
                            <Box display={"flex"} alignItems={"center"}>
                                {isDurationUpdated() ? (
                                    <>
                                        <MortgageSummaryLabel>
                                            {newMortgage.currency} {formatAmount(newMortgage.monthlyPayment)}
                                        </MortgageSummaryLabel>
                                        <MortgageTooltip
                                            title={t("General.This calculation is indicative and is based on...")}>
                                            <Info sx={{ fontSize: "16px", color: "#0093D1", marginLeft: "4px" }} />
                                        </MortgageTooltip>
                                    </>
                                ) : (
                                    <MortgageSummaryHeaderLabel>
                                        {t("ConfigurationScreen.Set term")}
                                    </MortgageSummaryHeaderLabel>
                                )}
                            </Box>
                        </Box>
                        <Box flex={1}>
                            <Box>
                                <MortgageSummaryHeaderLabel data-cy={"interest-rate"}>
                                    {t("General.Interest rate")}
                                </MortgageSummaryHeaderLabel>
                            </Box>
                            <Box display={"flex"} alignItems={"center"}>
                                {isDurationUpdated() ? (
                                    <>
                                        <MortgageSummaryLabel>
                                            {formatInterestRate(newMortgage.interestRate)} %
                                        </MortgageSummaryLabel>
                                        <MortgageTooltip
                                            title={t(
                                                "ConfigurationScreen.Interest rates may change over the course of the next few days..."
                                            )}>
                                            <Help sx={{ fontSize: "16px", color: "#0093D1", marginLeft: "4px" }} />
                                        </MortgageTooltip>
                                    </>
                                ) : (
                                    <MortgageSummaryHeaderLabel>
                                        {t("ConfigurationScreen.Set term")}
                                    </MortgageSummaryHeaderLabel>
                                )}
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{ marginTop: "16px", display: "flex", flexFlow: "row-reverse" }}>
                        <MortgageSummaryBold>
                            {newMortgage.currency} {formatAmount(newMortgage.amount)}
                        </MortgageSummaryBold>
                    </Box>
                </Box>
                <Divider />

                <Box sx={{ paddingTop: "16px", paddingBottom: "16px" }}>
                    <Box>
                        <MortgageSummaryBold>{t("General.Repayment")}</MortgageSummaryBold>
                    </Box>
                    <Box>
                        <MortgageSummaryLabel>
                            {t("General.From account", { account: newMortgage.positionCapital })}
                        </MortgageSummaryLabel>
                    </Box>
                    <Box>
                        <MortgageSummaryLabel>{parseIBAN(newMortgage.ibanCapital)}</MortgageSummaryLabel>
                    </Box>
                    <Box sx={{ marginTop: "16px", display: "flex", flexFlow: "row-reverse" }}>
                        <MortgageSummaryBold>
                            {newMortgage.currency} {formatAmount(newMortgage.repayment)}
                        </MortgageSummaryBold>
                    </Box>
                </Box>
                <Divider />

                <Box sx={{ paddingTop: "16px", paddingBottom: "16px" }}>
                    <Box display={"flex"} justifyContent={"space-between"}>
                        <MortgageSummaryBold>{t("ConfigurationScreen.Sum")}</MortgageSummaryBold>
                        <MortgageSummaryBold>
                            {newMortgage.currency} {formatAmount(newMortgage.totalSumLiabilities)}
                        </MortgageSummaryBold>
                    </Box>
                </Box>
                <Divider />

                <Box sx={{ paddingTop: "16px", paddingBottom: "16px" }}>
                    <Box>
                        <MortgageSummaryBold>{t("ConfigurationScreen.Other mortgage(s)")}</MortgageSummaryBold>
                        <MortgageSummaryLabel>
                            {t("ConfigurationScreen.Interest including amortisation")}
                        </MortgageSummaryLabel>
                    </Box>
                    <Box mt={"16px"}>
                        <Box>
                            <MortgageSummaryHeaderLabel>{t("General.Monthly charge")}</MortgageSummaryHeaderLabel>
                        </Box>
                        <Box>
                            <MortgageSummaryLabel>
                                {newMortgage.currency} {formatAmount(newMortgage.totalSumOtherLiabilitiesMonthly)}
                            </MortgageSummaryLabel>
                        </Box>
                    </Box>
                    <Box sx={{ marginTop: "16px", display: "flex", flexFlow: "row-reverse" }}>
                        <MortgageSummaryBold>
                            {newMortgage.currency} {formatAmount(newMortgage.totalSumOtherLiabilities)}
                        </MortgageSummaryBold>
                    </Box>
                </Box>
                <Divider />

                <Box sx={{ paddingTop: "16px", paddingBottom: "16px" }}>
                    <Box>
                        <MortgageSummaryBold>
                            {t("ConfigurationScreen.Total charge after mortgage extension (as of today)")}
                        </MortgageSummaryBold>
                        <MortgageSummaryLabel>
                            {t("ConfigurationScreen.Excluding additional costs")}
                        </MortgageSummaryLabel>
                        <DetailsLink underline="none" onClick={showDetailsDialog} role={"button"}>
                            {t("Actions.Show details")}
                        </DetailsLink>
                        <DetailsPopupScreen
                            fullScreen={true}
                            open={detailsDialogOpen}
                            closeDialogFunction={closeDetailsDialog}
                        />
                    </Box>
                    <Box display={"flex"} justifyContent={"space-between"} mt={"24px"}>
                        <Box display={"flex"} alignItems={"center"}>
                            <MortgageSummaryBold>{t("General.Monthly charge")}</MortgageSummaryBold>
                            <MortgageTooltip title={t("General.This calculation is indicative and is based on...")}>
                                <Info sx={{ fontSize: "16px", color: "#0093D1", marginLeft: "4px" }} />
                            </MortgageTooltip>
                        </Box>
                        <MortgageSummaryBold>
                            {newMortgage.currency} {formatAmount(newMortgage.totalSumLiabilitiesMonthly)}
                        </MortgageSummaryBold>
                    </Box>
                    <Box display={"flex"} justifyContent={"space-between"}>
                        <MortgageSummaryLabel>{t("General.Total amount")}</MortgageSummaryLabel>
                        <MortgageSummaryLabel>
                            {newMortgage.currency} {formatAmount(newMortgage.totalSumLiabilities)}
                        </MortgageSummaryLabel>
                    </Box>
                </Box>
            </Box>
        );
    };

    const renderWebView = () => {
        return (
            <TableContainer sx={{ overflowX: "hidden", marginTop: "40px" }} data-cy={"configuration-summary-web"}>
                <Table aria-label="new mortgage table">
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <MortgageSummaryHeaderLabel data-cy={"financing"}>
                                    {t("SummaryScreen.Financing")}
                                </MortgageSummaryHeaderLabel>
                            </TableCell>
                            <TableCell align="right">
                                <MortgageSummaryHeaderLabel data-cy={"amount-in-label"}>
                                    {t("General.Amount in", { currency: newMortgage.currency })}
                                </MortgageSummaryHeaderLabel>
                            </TableCell>
                            <TableCell align="right">
                                <Box display={"flex"} justifyContent={"flex-end"}>
                                    <MortgageSummaryHeaderLabel data-cy={"interest-rate-label"}>
                                        {t("General.Interest rate")}{" "}
                                    </MortgageSummaryHeaderLabel>
                                    <MortgageTooltip
                                        title={t(
                                            "ConfigurationScreen.Interest rates may change over the course of the next few days..."
                                        )}>
                                        <Help sx={{ fontSize: "16px", color: "#0093D1", marginLeft: "4px" }} />
                                    </MortgageTooltip>
                                </Box>
                            </TableCell>
                            <TableCell align="right">
                                <Box display={"flex"} justifyContent={"flex-end"}>
                                    <MortgageSummaryHeaderLabel data-cy={"monthly-charge-rate-label"}>
                                        {t("General.Monthly charge in", { currency: newMortgage.currency })}
                                    </MortgageSummaryHeaderLabel>
                                    <MortgageTooltip
                                        title={t("General.This calculation is indicative and is based on...")}>
                                        <Info sx={{ fontSize: "16px", color: "#0093D1", marginLeft: "4px" }} />
                                    </MortgageTooltip>
                                </Box>
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        <TableRow key="fixed-mortgage">
                            <TableCell component="th" scope="row">
                                <MortgageSummaryBold data-cy={"product-typ-body"}>
                                    {renderProductType(newMortgage, i18n, t)}
                                </MortgageSummaryBold>
                                <MortgageSummaryLabel data-cy={"summary-duration"}>
                                    {newMortgage.duration} {t("General.Years")} /{" "}
                                    {formatDate(newMortgage.expirationDate, DateFormat.DATE)} -{" "}
                                    {addYears(newMortgage.expirationDate, +newMortgage.duration)}
                                </MortgageSummaryLabel>
                            </TableCell>
                            <TableCell align="right">
                                <MortgageSummaryLabel data-cy={"new-mortgage-amount"}>
                                    {" "}
                                    {formatAmount(newMortgage.amount)}{" "}
                                </MortgageSummaryLabel>
                            </TableCell>
                            <TableCell align="right">
                                {isDurationUpdated() ? (
                                    <MortgageSummaryLabel data-cy={"summary-interest-rate"}>
                                        {formatInterestRate(newMortgage.interestRate)} %
                                    </MortgageSummaryLabel>
                                ) : (
                                    <MortgageSummaryHeaderLabel>
                                        {t("ConfigurationScreen.Set term")}
                                    </MortgageSummaryHeaderLabel>
                                )}
                            </TableCell>
                            <TableCell align="right">
                                {isDurationUpdated() ? (
                                    <MortgageSummaryLabel data-cy={"summary-monthly-payment"}>
                                        {formatAmount(newMortgage.monthlyPayment)}
                                    </MortgageSummaryLabel>
                                ) : (
                                    <MortgageSummaryHeaderLabel>
                                        {t("ConfigurationScreen.Set term")}
                                    </MortgageSummaryHeaderLabel>
                                )}
                            </TableCell>
                        </TableRow>

                        <TableRow key="Repayment">
                            <TableCell component="th" scope="row">
                                <MortgageSummaryBold data-cy={"repayment-label"}>
                                    {t("General.Repayment")}
                                </MortgageSummaryBold>
                                <MortgageSummaryLabel data-cy={"from-account-label"}>
                                    {t("General.From account iban", {
                                        account: newMortgage.positionCapital,
                                        iban: parseIBAN(newMortgage.ibanCapital),
                                    })}
                                </MortgageSummaryLabel>
                            </TableCell>
                            <TableCell align="right">
                                <MortgageSummaryLabel data-cy={"summary-repayment"}>
                                    {" "}
                                    {formatAmount(newMortgage.repayment)}{" "}
                                </MortgageSummaryLabel>
                            </TableCell>
                            <TableCell align="right"></TableCell>
                            <TableCell align="right"></TableCell>
                        </TableRow>

                        <TableRow key="Sum">
                            <TableCell component="th" scope="row">
                                <MortgageSummaryBold data-cy={"total-label"}>
                                    {t("ConfigurationScreen.Sum")}
                                </MortgageSummaryBold>
                            </TableCell>
                            <TableCell align="right">
                                <MortgageSummaryLabel data-cy={"summary-total"}>
                                    {" "}
                                    {formatAmount(newMortgage.amount)}{" "}
                                </MortgageSummaryLabel>
                            </TableCell>
                            <TableCell align="right"></TableCell>
                            <TableCell align="right"></TableCell>
                        </TableRow>

                        <TableRow key="Other-Liabilities">
                            <TableCell component="th" scope="row">
                                <MortgageSummaryBold data-cy={"other-mortgage-label"}>
                                    {t("ConfigurationScreen.Other mortgage(s)")}
                                </MortgageSummaryBold>
                                <MortgageSummaryLabel data-cy={"amortisation-label"}>
                                    {t("ConfigurationScreen.Interest including amortisation")}
                                </MortgageSummaryLabel>
                            </TableCell>
                            <TableCell align="right" data-cy={"total-sum-other-liabilities"}>
                                <MortgageSummaryLabel>
                                    {formatAmount(newMortgage.totalSumOtherLiabilities)}
                                </MortgageSummaryLabel>
                            </TableCell>
                            <TableCell align="right"></TableCell>
                            <TableCell align="right">
                                <MortgageSummaryLabel data-cy={"total-sum-other-liabilities-monthly"}>
                                    {formatAmount(newMortgage.totalSumOtherLiabilitiesMonthly)}
                                </MortgageSummaryLabel>
                            </TableCell>
                        </TableRow>

                        <TableRow key="Total-Sum">
                            <TableCell component="th" scope="row" sx={{ borderBottom: "none" }}>
                                <MortgageSummaryBold data-cy={"total-charge-text"}>
                                    {t("ConfigurationScreen.Total charge after mortgage extension (as of today)")}
                                </MortgageSummaryBold>

                                <MortgageSummaryLabel data-cy={"excluding-costs-text"}>
                                    {t("ConfigurationScreen.Excluding additional costs")}
                                </MortgageSummaryLabel>
                                <DetailsLink
                                    underline="none"
                                    onClick={showDetailsDialog}
                                    data-cy={"link-web-view-details"}
                                    role={"button"}>
                                    {t("Actions.Show details")}
                                </DetailsLink>

                                <DetailsPopupScreen open={detailsDialogOpen} closeDialogFunction={closeDetailsDialog} />
                            </TableCell>
                            <TableCell align="right" sx={{ borderBottom: "none" }}>
                                <MortgageSummaryBold data-cy={"summary-total-sum-liabilities"}>
                                    {" "}
                                    {formatAmount(newMortgage.totalSumLiabilities)}{" "}
                                </MortgageSummaryBold>
                            </TableCell>
                            <TableCell align="right" sx={{ borderBottom: "none" }}></TableCell>
                            <TableCell align="right" sx={{ borderBottom: "none" }}>
                                <MortgageSummaryBold data-cy={"summary-total-sum-liabilities-monthly"}>
                                    {formatAmount(newMortgage.totalSumLiabilitiesMonthly)}
                                </MortgageSummaryBold>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    return <Box>{matchesWebView ? renderWebView() : renderMobileView()}</Box>;
};
export default ConfigurationSummary;
